import { useEffect, useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Cpu, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * SpeedIQRearAnalysisRunner — client-side MediaPipe pose extraction over the
 * REAR-view sprint clip. Computes frontal-plane symmetry metrics:
 *   · pelvic_drop_max_deg     (contralateral pelvis dip)
 *   · knee_tracking_dev_deg   (medial knee deviation; valgus proxy)
 *   · foot_path_dev_in        (heel-recovery foot path deviation)
 *   · step_width_proxy_in     (contralateral foot strike width)
 *   · trunk_lateral_sway_deg  (frontal-plane trunk lean)
 *   · arm_swing_symmetry_pct  (L vs R arm-swing amplitude balance)
 *   · lower_body_symmetry_pct (L vs R stride-mechanics composite)
 *   · crossover_detected      (boolean — foot crosses body midline)
 *
 * Posts to `POST /speediq/sessions/{id}/analysis/rear`.
 * Mirrors the side-view runner architecture; shares the same MediaPipe lib.
 */
const MODEL_URL =
  "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task";
const WASM_BASE =
  "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";

const LM = {
  NOSE: 0,
  L_SHOULDER: 11, R_SHOULDER: 12,
  L_HIP: 23,      R_HIP: 24,
  L_KNEE: 25,     R_KNEE: 26,
  L_ANKLE: 27,    R_ANKLE: 28,
  L_HEEL: 29,     R_HEEL: 30,
  L_FOOT: 31,     R_FOOT: 32,
  L_ELBOW: 13,    R_ELBOW: 14,
  L_WRIST: 15,    R_WRIST: 16,
};

const deg = (rad) => (rad * 180) / Math.PI;
const dist2d = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);

export default function SpeedIQRearAnalysisRunner({ sessionId, rearAsset, onComplete }) {
  const [phase, setPhase] = useState("idle");   // idle | loading | running | uploading | done | error
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const videoRef = useRef(null);

  const run = async () => {
    setErr(null); setResult(null); setProgress(0); setPhase("loading");
    try {
      const { PoseLandmarker, FilesetResolver } = await import("@mediapipe/tasks-vision");
      const wasm = await FilesetResolver.forVisionTasks(WASM_BASE);
      const lm = await PoseLandmarker.createFromOptions(wasm, {
        baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
        runningMode: "VIDEO", numPoses: 1, minPoseDetectionConfidence: 0.5,
      });
      setPhase("running");
      const video = videoRef.current;
      await new Promise((resolve, reject) => {
        video.onloadedmetadata = () => resolve();
        video.onerror = (e) => reject(e);
        video.src = rearAsset?.preview_url || rearAsset?.url;
        video.load();
      });
      const fps = 30;
      const totalSec = Math.min(video.duration || 6, 12);
      const totalFrames = Math.floor(totalSec * fps);
      const frames = [];
      let confSum = 0, confCount = 0;
      for (let i = 0; i < totalFrames; i++) {
        const t = i / fps;
        video.currentTime = t;
        await new Promise((r) => (video.onseeked = r));
        const detect = lm.detectForVideo(video, performance.now());
        const pose = detect?.landmarks?.[0];
        if (pose) {
          frames.push({ t, pose });
          const visAvg = pose.reduce((acc, p) => acc + (p.visibility || 0), 0) / pose.length;
          confSum += visAvg; confCount += 1;
        }
        setProgress(Math.round(100 * (i + 1) / totalFrames));
      }
      const metrics = computeRearMetrics(frames);
      metrics.frame_count = frames.length;
      metrics.fps_effective = fps;
      metrics.capture_confidence = confCount ? confSum / confCount : 0;
      metrics.engine_version = "speediq.rear.v1";

      setPhase("uploading");
      const { data } = await api.post(`/speediq/sessions/${sessionId}/analysis/rear`, metrics);
      setResult({ ...data, metrics });
      setPhase("done");
      toast.success(`Rear analysis · ${data.flag_count} flag(s)`);
      onComplete?.(data, metrics);
    } catch (e) {
      console.error("[speediq-rear-runner]", e);
      setErr(e.message || String(e));
      setPhase("error");
      toast.error("Rear analysis failed — check console");
    }
  };

  return (
    <Card className="p-4 bg-[var(--ink-2)] border-[var(--ink-border)]" data-testid="speediq-rear-analysis-runner">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-white/55">Rear-view pose pipeline</div>
          <div className="font-display font-bold text-white text-sm">Symmetry · pelvic drop · knee tracking · crossover</div>
        </div>
        <Button onClick={run} disabled={phase === "running" || phase === "loading" || phase === "uploading"}
          className="text-white red-glow" style={{ backgroundColor: "var(--brand-secondary)" }}
          data-testid="speediq-rear-run">
          {phase === "idle" && <>Run rear-view analysis</>}
          {(phase === "loading" || phase === "running" || phase === "uploading") && (
            <><Loader2 className="h-4 w-4 animate-spin mr-2" />{phase === "loading" ? "Loading model" : phase === "uploading" ? "Saving" : `Analyzing ${progress}%`}</>
          )}
          {phase === "done" && <>Re-run</>}
          {phase === "error" && <>Try again</>}
        </Button>
      </div>
      <video ref={videoRef} className="hidden" muted playsInline crossOrigin="anonymous" />
      {phase === "running" && (
        <div className="mt-3 h-1.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full transition-all" style={{ width: `${progress}%`, backgroundColor: "var(--brand-secondary)" }} />
        </div>
      )}
      {phase === "done" && result && (
        <div className="mt-3 text-[12px] text-white/65" data-testid="speediq-rear-result">
          <div className="inline-flex items-center gap-1.5 font-bold text-emerald-300">
            <Cpu className="h-3 w-3" /> {result.flag_count} rear flag(s)
          </div>
          <div className="text-[11px] text-white/45 mt-1">
            Engine v1 · confidence {(result.metrics.capture_confidence * 100).toFixed(0)}%
          </div>
        </div>
      )}
      {phase === "error" && (
        <div className="mt-3 text-[12px] text-amber-300 inline-flex items-center gap-1.5">
          <AlertCircle className="h-3.5 w-3.5" /> {err}
        </div>
      )}
    </Card>
  );
}


// ── Metric extraction helpers ───────────────────────────────────────
function computeRearMetrics(frames) {
  const m = {
    pelvic_drop_max_deg: null,
    knee_tracking_dev_deg: null,
    pronation_asymmetry_pct: null,
    foot_path_dev_in: null,
    step_width_proxy_in: null,
    trunk_lateral_sway_deg: null,
    arm_swing_symmetry_pct: null,
    lower_body_symmetry_pct: null,
    frontal_plane_control_score: null,
    rear_view_stability_score: null,
    crossover_detected: false,
  };
  if (!frames.length) return m;

  const pelvicDrops = [];
  const trunkSways = [];
  const kneeDevs = [];
  const leftArmRanges = [];
  const rightArmRanges = [];
  const leftAnkles = [];
  const rightAnkles = [];

  for (const f of frames) {
    const p = f.pose;
    const lH = p[LM.L_HIP], rH = p[LM.R_HIP];
    const lS = p[LM.L_SHOULDER], rS = p[LM.R_SHOULDER];
    const lK = p[LM.L_KNEE], rK = p[LM.R_KNEE];
    const lA = p[LM.L_ANKLE], rA = p[LM.R_ANKLE];

    // Pelvic drop: angle between hip line and horizontal
    if (lH && rH) {
      const ang = Math.abs(deg(Math.atan2(rH.y - lH.y, rH.x - lH.x)));
      pelvicDrops.push(ang);
    }
    // Trunk lateral sway: shoulder midpoint vs hip midpoint horizontal offset
    if (lS && rS && lH && rH) {
      const shoMid = { x: (lS.x + rS.x) / 2, y: (lS.y + rS.y) / 2 };
      const hipMid = { x: (lH.x + rH.x) / 2, y: (lH.y + rH.y) / 2 };
      const sway = deg(Math.atan2(shoMid.x - hipMid.x, hipMid.y - shoMid.y));
      trunkSways.push(Math.abs(sway));
    }
    // Knee tracking deviation: knee-x vs hip-ankle line midpoint
    if (lH && lK && lA) {
      const midX = (lH.x + lA.x) / 2;
      kneeDevs.push(Math.abs(lK.x - midX) * 100);  // normalized → degrees-ish
    }
    if (rH && rK && rA) {
      const midX = (rH.x + rA.x) / 2;
      kneeDevs.push(Math.abs(rK.x - midX) * 100);
    }
    // Arm swing range proxies
    if (p[LM.L_WRIST] && p[LM.L_SHOULDER]) leftArmRanges.push(dist2d(p[LM.L_WRIST], p[LM.L_SHOULDER]));
    if (p[LM.R_WRIST] && p[LM.R_SHOULDER]) rightArmRanges.push(dist2d(p[LM.R_WRIST], p[LM.R_SHOULDER]));
    if (lA) leftAnkles.push(lA.x);
    if (rA) rightAnkles.push(rA.x);
  }
  m.pelvic_drop_max_deg = pelvicDrops.length ? Math.max(...pelvicDrops) : null;
  m.trunk_lateral_sway_deg = trunkSways.length ? Math.max(...trunkSways) : null;
  m.knee_tracking_dev_deg = kneeDevs.length ? avg(kneeDevs) : null;

  if (leftArmRanges.length && rightArmRanges.length) {
    const l = range(leftArmRanges); const r = range(rightArmRanges);
    if (l && r) m.arm_swing_symmetry_pct = Math.round((Math.min(l, r) / Math.max(l, r)) * 100);
  }
  if (leftAnkles.length && rightAnkles.length) {
    const lr = range(leftAnkles); const rr = range(rightAnkles);
    if (lr && rr) m.lower_body_symmetry_pct = Math.round((Math.min(lr, rr) / Math.max(lr, rr)) * 100);
  }
  // Crossover: any frame where ankles cross the hip-midline
  if (leftAnkles.length && rightAnkles.length) {
    for (let i = 0; i < frames.length; i++) {
      const p = frames[i].pose;
      const lH = p[LM.L_HIP], rH = p[LM.R_HIP], lA = p[LM.L_ANKLE], rA = p[LM.R_ANKLE];
      if (lH && rH && lA && rA) {
        const midX = (lH.x + rH.x) / 2;
        if ((lA.x > midX && Math.abs(lA.x - midX) > 0.02) || (rA.x < midX && Math.abs(rA.x - midX) > 0.02)) {
          m.crossover_detected = true; break;
        }
      }
    }
  }
  // Composite scores
  if (m.pelvic_drop_max_deg != null && m.trunk_lateral_sway_deg != null) {
    m.frontal_plane_control_score = Math.max(0, Math.min(100,
      100 - m.pelvic_drop_max_deg * 5 - m.trunk_lateral_sway_deg * 4));
    m.rear_view_stability_score = Math.max(0, Math.min(100,
      100 - m.pelvic_drop_max_deg * 4 - (m.knee_tracking_dev_deg || 0) * 2));
  }
  // Step width proxy
  if (leftAnkles.length && rightAnkles.length) {
    const lMin = Math.min(...leftAnkles); const lMax = Math.max(...leftAnkles);
    const rMin = Math.min(...rightAnkles); const rMax = Math.max(...rightAnkles);
    m.step_width_proxy_in = Math.round((Math.abs((lMin + lMax) / 2 - (rMin + rMax) / 2)) * 60); // rough pixel→inch proxy
  }
  m.foot_path_dev_in = m.crossover_detected ? 3.0 : 1.5;   // placeholder until landmark trail analysis
  m.pronation_asymmetry_pct = 0;                            // requires sub-talar metrics — V2

  return m;
}

const avg = (arr) => arr.reduce((a, b) => a + b, 0) / arr.length;
const range = (arr) => arr.length ? Math.max(...arr) - Math.min(...arr) : null;
